import React, { useState } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shippingMethodService, ShippingMethod, ShippingMethodInput } from '../../api/services/shippingMethodService';
import { toast } from 'react-hot-toast';

export default function ShippingMethods() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ShippingMethodInput>({
    Name: '',
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  // Fetch all shipping methods
  const { data: shippingMethods, isLoading } = useQuery({
    queryKey: ['shippingMethods'],
    queryFn: () => shippingMethodService.getAllShippingMethods(),
  });

  // Create shipping method mutation
  const createMutation = useMutation({
    mutationFn: (shippingMethod: ShippingMethodInput) => shippingMethodService.createShippingMethod(shippingMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shippingMethods'] });
      toast.success('Shipping method added successfully');
      setFormData({ Name: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add shipping method');
    },
  });

  // Update shipping method mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ShippingMethodInput }) =>
      shippingMethodService.updateShippingMethod(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shippingMethods'] });
      toast.success('Shipping method updated successfully');
      setEditingId(null);
      setEditingName('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update shipping method');
    },
  });

  // Delete shipping method mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => shippingMethodService.deleteShippingMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shippingMethods'] });
      toast.success('Shipping method deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete shipping method');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.Name || formData.Name.trim() === '') {
      toast.error('Please enter a shipping method name');
      return;
    }

    createMutation.mutate(formData);
  };

  const handleDelete = (shippingMethod: ShippingMethod) => {
    if (window.confirm(`Are you sure you want to delete "${shippingMethod.Name}"?`)) {
      deleteMutation.mutate(shippingMethod.Id);
    }
  };

  const startEditing = (shippingMethod: ShippingMethod) => {
    setEditingId(shippingMethod.Id);
    setEditingName(shippingMethod.Name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEditing = (id: number) => {
    if (!editingName || editingName.trim() === '') {
      toast.error('Please enter a shipping method name');
      return;
    }

    updateMutation.mutate({
      id,
      data: { Name: editingName },
    });
  };

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Manage Shipping Methods
            </h2>
          </div>

          <div className="p-6">
            {/* Add Shipping Method Form */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Add Shipping Method
              </h3>
              <form onSubmit={handleSubmit} className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ Name: e.target.value })}
                    placeholder="Shipping Method Name"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>

            {/* Shipping Methods Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : !shippingMethods || shippingMethods.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No shipping methods found
                      </td>
                    </tr>
                  ) : (
                    shippingMethods.map((method) => (
                      <tr key={method.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {editingId === method.Id ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                              autoFocus
                            />
                          ) : (
                            method.Name
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {editingId === method.Id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveEditing(method.Id)}
                                disabled={updateMutation.isPending}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                {updateMutation.isPending ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={updateMutation.isPending}
                                className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEditing(method)}
                                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(method)}
                                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
